/**
 * Circling Raven Golf Club — Worley, Idaho.
 *
 * Gene Bates, 2003, at the Coeur d'Alene Casino Resort. Played by the
 * tournament in 2024 and 2025.
 *
 * Transcribed 2026-07-29 from public scorecard databases, NOT from the physical
 * card. Nothing here has been matched against the card in the pro shop, so this
 * ships unverified like everything else in `seed.ts`.
 *
 * Note on tee ids: Circling Raven's tees are Gold / Blue / White / Red. The ids
 * below are deliberately the four `YARD_TEES` slot keys from `seed.ts` —
 * black/green/gold/bronze, longest to shortest — so that `cardHoles` lands each
 * yardage row on the tee it actually belongs to. The club's real tee names live
 * in `name`. Do not read the ids as colors here: the tee named Gold is the
 * tips and carries the id `black`, and the id `gold` is the White tee.
 */

import { tees, type Seed } from '../card'

export const CIRCLING_RAVEN: Seed = {
  id: 'circling-raven',
  name: 'Circling Raven',
  par: 72,
  pars: [5, 4, 3, 4, 5, 4, 3, 4, 4, 4, 4, 5, 3, 4, 4, 3, 5, 4],
  si: [7, 11, 15, 1, 5, 13, 17, 9, 3, 14, 6, 2, 12, 18, 8, 16, 4, 10],
  yards: [
    [513, 395, 217, 406, 574, 387, 212, 386, 474, 336, 446, 581, 253, 397, 426, 192, 559, 435],
    [493, 349, 186, 380, 529, 351, 185, 357, 451, 321, 418, 535, 218, 328, 391, 176, 519, 392],
    [483, 310, 182, 360, 501, 317, 154, 329, 437, 298, 379, 488, 200, 306, 369, 162, 486, 347],
    [466, 275, 163, 268, 405, 272, 149, 298, 397, 269, 347, 436, 145, 269, 348, 137, 431, 314],
  ],
  tees: tees([
    ['black', 'Gold', 74.1, 144, 7189],
    ['green', 'Blue', 71.3, 138, 6579],
    ['gold', 'White', 69.6, 131, 6108],
    ['bronze', 'Red', 65.3, 113, 5389],
  ]),
  provenance:
    'The best-behaved card in this repo, for whatever that is worth against a ' +
    'card nobody has photographed. Par and stroke index agree hole-for-hole ' +
    'across three databases — golfpass, 18birdies and hole19 — and hole19 is not ' +
    'simply a copy of the other two: it stores the card in meters, and its 6,016 ' +
    'converts back to the 6,579-yard Blue tee. 36 out, 36 in, 72; the stroke ' +
    'index is a clean permutation of 1-18 with the odds on the front and the ' +
    'evens on the back. Yardages are golfpass, and every one of the ten ' +
    'nine-hole subtotals sums exactly to its printed total (7,189 / 6,579 / ' +
    '6,108 / 5,389), with no tee ever leapfrogging a longer one on any hole. ' +
    'Two things were thrown away rather than reconciled. Golflink’s hole table ' +
    'for this course is corrupt and is not used at all: its par column sums to ' +
    '71 rather than 72 and its White column is shifted, giving a 182-yard par 4 ' +
    'at hole 3 and a 360-yard par 3 at hole 4 — the same column-shift failure ' +
    'that has already bitten this repo once. And the two forward tees have their ' +
    'names swapped between sources: golflink and 18birdies call the 5,389-yard ' +
    'tee Red and the 4,708-yard tee Green, while golfpass calls them Green and ' +
    'Red. This file follows the majority and names 5,389 as Red, so if the card ' +
    'says otherwise it is the label that is wrong, not the numbers. The ' +
    '4,708-yard tee is left out entirely because its published men’s rating ' +
    '(66.4/113) is higher than the longer 5,389-yard tee’s (65.3/113), which ' +
    'cannot be right, and a bad rating silently mis-sets a course handicap.',
}
